import React from 'react';
import cs from 'classnames';
import { injectV2 } from '../../../injectV2';
import { ForYouPageSection } from '../ForYouPageSection/ForYouPageSection';
import { ForYouTemplatesGrid } from '../ForYouTemplatesGrid/ForYouTemplatesGrid';
import { ForYouPageStore } from '../../../stores/ForYouPageStore';
import { RoutingStore } from '../../../stores/RoutingStore';
import { Category } from '../../../../../types/api';
import { RouteLocation } from '../../../stores/routes/createRoute';
import { ForYouCategoriesList } from '../ForYouCategoriesList/ForYouCategoriesList';
import { CategoriesStore } from '../../../stores/CategoriesStore';
import { UiLoggerStore } from '../../../stores/UiLoggerStore';
import { FedopsLoggerStore } from '../../../stores/FedopsLoggerStore';
import { WithTranslations, withTranslations } from '../../../../utils/withTranslations';
import { forYouGenericPageDataHooks } from './ForYouGenericPage.dataHooks';

interface ForYouGenericPageProps extends WithTranslations {
  dataHook?: string;
  forYouPageStore: ForYouPageStore;
  routingStore: RoutingStore;
  categoriesStore: CategoriesStore;
  uiLoggerStore: UiLoggerStore;
  fedopsLoggerStore: FedopsLoggerStore;
}

const ForYouGenericPageCmp: React.FC<ForYouGenericPageProps> = ({
  dataHook,
  forYouPageStore,
  routingStore,
  categoriesStore,
  uiLoggerStore,
  fedopsLoggerStore,
  t,
}) => {
  const {
    allTemplates,
    businessTemplates,
    onlineStoreTemplates,
    designTemplates,
    educationTemplates,
    blogTemplates,
    blankTemplates,
    listCategories,
  } = forYouPageStore;
  const { getCategoryBySlug } = categoriesStore;

  const getCategoryLink = (category?: Category): RouteLocation => {
    return category
      ? routingStore.locationBuilders.category({ categorySlug: category.name })
      : routingStore.locationBuilders.home({});
  };

  const allTemplatesCategory = getCategoryBySlug('all');
  const businessCategory = getCategoryBySlug('business');
  const onlineStoreCategory = getCategoryBySlug('online-store');
  const designCategory = getCategoryBySlug('design');
  const educationCategory = getCategoryBySlug('education');
  const blogCategory = getCategoryBySlug('blog');
  const blankCategory = getCategoryBySlug('blank');
  const listCategoriesItems = listCategories.filter(Boolean);
  const interaction = uiLoggerStore.initInteraction('for-you-page');

  React.useEffect(() => {
    fedopsLoggerStore.startForYouPageSwitchInteraction();
    uiLoggerStore.logStartedLoadingThePage(interaction.key);
  });

  React.useEffect(() => {
    if (forYouPageStore.isLoaded) {
      fedopsLoggerStore.stopActiveInteraction();
      uiLoggerStore.logFinishedLoadingThePage(interaction.key);
      uiLoggerStore.logGenericPageView();
    }
  }, [fedopsLoggerStore, forYouPageStore.isLoaded, interaction.key, uiLoggerStore]);

  return (
    <div data-hook={dataHook}>
      <ForYouPageSection
        expandLink={getCategoryLink(businessCategory)}
        title={t('forYouPage.category.title', { displayName: businessCategory?.displayName })}
        categorySlug={businessCategory?.name}
        dataHook={cs(forYouGenericPageDataHooks.section({ name: businessCategory?.name }))}
      >
        <ForYouTemplatesGrid
          templates={businessTemplates}
          categorySlug={businessCategory?.name}
          dataHook={forYouGenericPageDataHooks.sectionTemplates({ name: businessCategory?.name })}
        />
      </ForYouPageSection>
      <ForYouPageSection
        expandLink={getCategoryLink(onlineStoreCategory)}
        title={t('forYouPage.category.title', { displayName: onlineStoreCategory?.displayName })}
        dataHook={cs(forYouGenericPageDataHooks.section({ name: onlineStoreCategory?.name }))}
        categorySlug={onlineStoreCategory?.name}
      >
        <ForYouTemplatesGrid
          templates={onlineStoreTemplates}
          categorySlug={onlineStoreCategory?.name}
          dataHook={forYouGenericPageDataHooks.sectionTemplates({ name: onlineStoreCategory?.name })}
        />
      </ForYouPageSection>
      <ForYouPageSection
        title={t('forYouPage.categoryTags.title')}
        dataHook={forYouGenericPageDataHooks.section({ name: 'categories-list' })}
        isLargePadding={true}
      >
        <ForYouCategoriesList
          categories={listCategoriesItems}
          dataHook={forYouGenericPageDataHooks.sectionCategoriesList()}
        />
      </ForYouPageSection>
      <ForYouPageSection
        expandLink={getCategoryLink(designCategory)}
        title={t('forYouPage.category.title', { displayName: designCategory?.displayName })}
        dataHook={cs(forYouGenericPageDataHooks.section({ name: designCategory?.name }))}
        categorySlug={designCategory?.name}
      >
        <ForYouTemplatesGrid
          templates={designTemplates}
          categorySlug={designCategory?.name}
          dataHook={forYouGenericPageDataHooks.sectionTemplates({ name: designCategory?.name })}
        />
      </ForYouPageSection>
      <ForYouPageSection
        expandLink={getCategoryLink(educationCategory)}
        title={t('forYouPage.category.title', { displayName: educationCategory?.displayName })}
        dataHook={cs(forYouGenericPageDataHooks.section({ name: educationCategory?.name }))}
        categorySlug={educationCategory?.name}
      >
        <ForYouTemplatesGrid
          templates={educationTemplates}
          categorySlug={educationCategory?.name}
          dataHook={forYouGenericPageDataHooks.sectionTemplates({ name: educationCategory?.name })}
        />
      </ForYouPageSection>
      <ForYouPageSection
        expandLink={getCategoryLink(blogCategory)}
        title={t('forYouPage.category.title', { displayName: blogCategory?.displayName })}
        dataHook={cs(forYouGenericPageDataHooks.section({ name: blogCategory?.name }))}
        categorySlug={blogCategory?.name}
      >
        <ForYouTemplatesGrid
          templates={blogTemplates}
          categorySlug={blogCategory?.name}
          dataHook={forYouGenericPageDataHooks.sectionTemplates({ name: blogCategory?.name })}
        />
      </ForYouPageSection>
      <ForYouPageSection
        expandLink={getCategoryLink(blankCategory)}
        title={t('forYouPage.category.title.blank')}
        dataHook={cs(forYouGenericPageDataHooks.section({ name: blankCategory?.name }))}
        categorySlug={blankCategory?.name}
      >
        <ForYouTemplatesGrid
          templates={blankTemplates}
          categorySlug={blankCategory?.name}
          dataHook={forYouGenericPageDataHooks.sectionTemplates({ name: blankCategory?.name })}
        />
      </ForYouPageSection>
      <ForYouPageSection
        expandLink={getCategoryLink(allTemplatesCategory)}
        title={t('forYouPage.category.title.all')}
        dataHook={cs(forYouGenericPageDataHooks.section({ name: allTemplatesCategory?.name }))}
        categorySlug={allTemplatesCategory?.name}
      >
        <ForYouTemplatesGrid
          templates={allTemplates}
          categorySlug={allTemplatesCategory?.name}
          dataHook={forYouGenericPageDataHooks.sectionTemplates({ name: allTemplatesCategory?.name })}
        />
      </ForYouPageSection>
    </div>
  );
};
export const ForYouGenericPage = withTranslations(
  injectV2(
    'forYouPageStore',
    'routingStore',
    'categoriesStore',
    'uiLoggerStore',
    'fedopsLoggerStore',
  )(ForYouGenericPageCmp),
);
